import { useEffect, useMemo, useState, type ComponentType, type PropsWithChildren, type ReactNode } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { env } from '@/lib/env';
import { getCopilotRuntimeInfo, getRuntimeTarget } from '@/lib/runtime';
import { useAssistantStore } from '@/state/assistant-store';
import { useSessionStore } from '@/state/session-store';

type CopilotKitComponent = ComponentType<{
  runtimeUrl: string;
  headers: Record<string, string>;
  properties: Record<string, unknown>;
  showDevConsole: boolean;
  threadId: string;
  forwardedParameters: {
    temperature: number;
  };
  children: ReactNode;
}>;

export function DesktopCopilotProvider({ children }: PropsWithChildren) {
  const [loaded, setLoaded] = useState(false);
  const [CopilotKit, setCopilotKit] = useState<CopilotKitComponent | null>(null);
  const runtimeInfo = useAssistantStore((state) => state.runtimeInfo);
  const currentRoute = useAssistantStore((state) => state.currentRoute);
  const setRuntimeInfo = useAssistantStore((state) => state.setRuntimeInfo);
  const threadId = useAssistantStore((state) => state.threadId);
  const { session, activeOrganizationId, user } = useSessionStore(
    useShallow((state) => ({
      session: state.session,
      activeOrganizationId: state.activeOrganizationId,
      user: state.user,
    })),
  );
  const runtimeTarget = getRuntimeTarget();

  useEffect(() => {
    let mounted = true;

    void (async () => {
      try {
        const info = getCopilotRuntimeInfo();

        if (!mounted) {
          return;
        }

        if (!info.enabled || !info.runtimeUrl) {
          setRuntimeInfo(info);
          setLoaded(true);
          return;
        }

        const module = await import('@copilotkit/react-core');

        if (!mounted) {
          return;
        }

        setCopilotKit(() => module.CopilotKit as CopilotKitComponent);
        setRuntimeInfo(info);
        setLoaded(true);
      } catch {
        if (!mounted) {
          return;
        }

        setRuntimeInfo({
          enabled: false,
          runtimeUrl: null,
          model: null,
          reason: 'Unable to initialize the configured Copilot runtime.',
        });
        setLoaded(true);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [setRuntimeInfo]);

  const headers = useMemo(() => {
    const nextHeaders: Record<string, string> = {};

    if (session?.access_token) {
      nextHeaders.Authorization = `Bearer ${session.access_token}`;
    }

    if (activeOrganizationId) {
      nextHeaders['x-nordic-org-id'] = activeOrganizationId;
    }

    if (user?.id) {
      nextHeaders['x-nordic-user-id'] = user.id;
    }

    if (currentRoute) {
      nextHeaders['x-nordic-route'] = currentRoute;
    }

    return nextHeaders;
  }, [activeOrganizationId, currentRoute, session?.access_token, user?.id]);

  const properties = useMemo(
    () => ({
      organizationId: activeOrganizationId,
      route: currentRoute,
      userId: user?.id ?? null,
      metadata: {
        requestType: `${runtimeTarget}-global-copilot`,
      },
    }),
    [activeOrganizationId, currentRoute, runtimeTarget, user?.id],
  );

  if (!loaded || !CopilotKit || !runtimeInfo?.enabled || !runtimeInfo.runtimeUrl) {
    return <>{children}</>;
  }

  return (
    <CopilotKit
      runtimeUrl={runtimeInfo.runtimeUrl}
      headers={headers}
      properties={properties}
      showDevConsole={import.meta.env.DEV && env.VITE_APP_ENV !== 'production'}
      threadId={threadId}
      forwardedParameters={{ temperature: 0.2 }}
    >
      {children}
    </CopilotKit>
  );
}
